
import React, { useMemo } from 'react';
import { Card } from "@/components/ui/card";
import { Brain, Info } from 'lucide-react';
import { 
  ResponsiveContainer, 
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  ScatterChart,
  Scatter,
  ZAxis,
  Label
} from 'recharts';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { PerformanceData } from './types';
import { useIsMobile } from '@/hooks/use-mobile';

interface CognitiveLoadIndexProps {
  performanceData: PerformanceData[];
}

// Define chartConfig before it's used in useMemo
const chartConfig = {
  cognitiveLoad: { 
    label: "Cognitive Load Index", 
    color: "#8b5cf6"
  },
  difficulty: {
    label: "Difficulty Impact",
    color: "#f43f5e"
  },
  timeIntensity: {
    label: "Time Intensity",
    color: "#0ea5e9"
  },
  accuracyInverse: {
    label: "Error Rate",
    color: "#f97316"
  }
};

const getDifficultyWeight = (difficulty: string): number => {
  switch (difficulty.toLowerCase()) {
    case 'hard': return 3;
    case 'medium': return 2;
    case 'easy': 
    default: return 1;
  }
};

const CognitiveLoadIndex: React.FC<CognitiveLoadIndexProps> = ({ performanceData }) => {
  const { isMobile, screenSize } = useIsMobile();
  
  const cognitiveLoadData = useMemo(() => {
    if (!performanceData || performanceData.length === 0) return [];
    
    const subjectData: Record<string, {
      attempts: number,
      totalTimeSpent: number,
      totalDifficultyWeight: number,
      totalAccuracy: number,
      totalCognitiveLoad: number,
      lastAttemptDate: Date
    }> = {};
    
    performanceData.forEach(entry => {
      if (!entry.subject) return;
      
      if (!subjectData[entry.subject]) {
        subjectData[entry.subject] = {
          attempts: 0,
          totalTimeSpent: 0,
          totalDifficultyWeight: 0,
          totalAccuracy: 0,
          totalCognitiveLoad: 0,
          lastAttemptDate: new Date(entry.created_at)
        };
      }
      
      const difficultyWeight = getDifficultyWeight(entry.difficulty);
      const accuracy = entry.total_questions > 0 ? 
        (entry.correct_answers / entry.total_questions) : 0;
      
      // Calculate time spent based on total questions and difficulty
      const estimatedTimeSpent = Math.max(1, entry.total_questions * difficultyWeight * 0.5);
      
      subjectData[entry.subject].attempts++;
      subjectData[entry.subject].totalTimeSpent += estimatedTimeSpent;
      subjectData[entry.subject].totalDifficultyWeight += difficultyWeight;
      subjectData[entry.subject].totalAccuracy += accuracy;
      
      const cognitiveLoad = accuracy > 0 ? 
        (estimatedTimeSpent * difficultyWeight) / accuracy : 
        estimatedTimeSpent * difficultyWeight * 2;
        
      subjectData[entry.subject].totalCognitiveLoad += cognitiveLoad;
      
      // Update the last attempt date if this entry is more recent
      const entryDate = new Date(entry.created_at);
      if (entryDate > subjectData[entry.subject].lastAttemptDate) {
        subjectData[entry.subject].lastAttemptDate = entryDate;
      }
    });

    const result = Object.entries(subjectData)
      .filter(([_, data]) => data.attempts > 0)
      .map(([subject, data]) => ({
        subject,
        cognitiveLoad: Math.min(100, Math.round(data.totalCognitiveLoad / data.attempts)),
        difficulty: Math.min(100, Math.round((data.totalDifficultyWeight / data.attempts) * 33.3)),
        timeIntensity: Math.min(100, Math.round((data.totalTimeSpent / data.attempts) * 10)),
        accuracyInverse: Math.min(100, Math.round((1 - (data.totalAccuracy / data.attempts)) * 100)),
        size: Math.max(30, data.attempts * 5),
        lastAttemptDate: data.lastAttemptDate
      }))
      // Sort by most recent
      .sort((a, b) => b.lastAttemptDate.getTime() - a.lastAttemptDate.getTime())
      // Limit to 5 most recent subjects
      .slice(0, 5);
      
    return result;
  }, [performanceData]);

  const scatterData = useMemo(() => {
    if (!cognitiveLoadData || cognitiveLoadData.length === 0) return [];
    
    return cognitiveLoadData.map(item => ({
      x: item.difficulty,
      y: item.timeIntensity,
      z: item.accuracyInverse,
      subject: item.subject,
      cognitiveLoad: item.cognitiveLoad,
      size: item.size
    }));
  }, [cognitiveLoadData]);

  const enhancedChartData = useMemo(() => {
    if (!cognitiveLoadData || cognitiveLoadData.length === 0) return [];
    
    return cognitiveLoadData.map(item => ({
      subject: item.subject,
      "Cognitive Load": item.cognitiveLoad,
      "Time Intensity": item.timeIntensity,
      "Difficulty": item.difficulty,
      fill: chartConfig.cognitiveLoad.color
    }));
  }, [cognitiveLoadData]);

  // Generate random IDs for gradient definitions
  const gradientId1 = useMemo(() => `greenGradient-${Math.random().toString(36).substring(2, 9)}`, []);
  const gradientId2 = useMemo(() => `redGradient-${Math.random().toString(36).substring(2, 9)}`, []);
  const gradientId3 = useMemo(() => `blueGradient-${Math.random().toString(36).substring(2, 9)}`, []);
  const gradientId4 = useMemo(() => `orangeGradient-${Math.random().toString(36).substring(2, 9)}`, []);

  return (
    <Card className="p-4 sm:p-6 shadow-md bg-white overflow-hidden">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className={`${isMobile ? "text-lg" : "text-xl"} font-semibold mb-2 flex items-center gap-2`}>
            <Brain className="w-5 h-5 text-violet-500" />
            Cognitive Load Index
          </h2>
          <p className={`text-gray-500 ${isMobile ? "text-xs" : "text-sm"}`}>
            Mental effort across your 5 most recent subjects
          </p>
        </div>
        <TooltipProvider>
          <UITooltip>
            <TooltipTrigger asChild>
              <div className="cursor-help">
                <Info className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-sm">
                The Cognitive Load Index measures how much mental effort is required for each subject
                based on difficulty, time spent, and accuracy.
              </p>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      </div>
      
      {cognitiveLoadData.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <Brain className="w-12 h-12 text-gray-300 mb-4" />
          <p className="text-gray-500 text-center mb-2">No cognitive load data available yet</p>
          <p className={`${isMobile ? "text-xs" : "text-sm"} text-gray-400 text-center max-w-md`}>
            Complete quizzes across different topics to analyze your cognitive load patterns
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-4">
            <div className={`h-64 sm:h-80 bg-white rounded-lg shadow-sm border border-gray-100 p-3 ${isMobile ? 'p-2' : 'p-4'}`}>
              <h3 className={`${isMobile ? "text-xs" : "text-sm"} font-medium mb-4 text-gray-700`}>Subject Breakdown</h3>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={enhancedChartData}
                  margin={{ top: 10, right: 20, left: 0, bottom: isMobile ? 40 : 20 }}
                  barSize={isMobile ? 10 : 20}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="subject"
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    interval={0}
                    tick={{ 
                      fill: '#1f2937',
                      fontSize: isMobile ? 10 : 12,
                      fontWeight: 500
                    }}
                    tickMargin={10}
                  />
                  <YAxis
                    tickMargin={8}
                    tick={{ 
                      fill: '#1f2937',
                      fontSize: isMobile ? 10 : 12 
                    }}
                    domain={[0, 100]}
                  >
                    <Label
                      value="Score"
                      angle={-90}
                      position="insideLeft"
                      style={{
                        textAnchor: 'middle',
                        fill: '#1f2937',
                        fontSize: isMobile ? 12 : 14,
                        fontWeight: 500
                      }}
                    />
                  </YAxis>
                  <Tooltip
                    cursor={{ fillOpacity: 0.1 }}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      fontSize: isMobile ? '10px' : '12px'
                    }}
                    labelStyle={{
                      color: '#1f2937',
                      fontWeight: 600,
                      marginBottom: '4px',
                      fontSize: isMobile ? '10px' : '12px'
                    }}
                    itemStyle={{
                      color: '#4b5563',
                      fontSize: isMobile ? '10px' : '12px',
                      padding: '2px 0'
                    }}
                    formatter={(value: any) => [`${value}`, ``]}
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    iconSize={isMobile ? 8 : 12}
                    iconType="circle"
                    wrapperStyle={{
                      paddingBottom: isMobile ? '5px' : '10px',
                      fontSize: isMobile ? '10px' : '12px'
                    }}
                  />
                  <Bar
                    dataKey="Cognitive Load"
                    fill={chartConfig.cognitiveLoad.color}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="Time Intensity"
                    fill={chartConfig.timeIntensity.color}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="Difficulty"
                    fill={chartConfig.difficulty.color}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className={`h-64 sm:h-80 bg-white rounded-lg shadow-sm border border-gray-100 ${isMobile ? 'p-2' : 'p-4'} relative`}>
              <h3 className={`${isMobile ? "text-xs" : "text-base"} font-medium mb-4 text-gray-800`}>Cognitive Load Matrix</h3>
              
              <svg width="0" height="0">
                <defs>
                  <linearGradient id={gradientId1} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#34d399" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#34d399" stopOpacity={0.8} />
                  </linearGradient>
                  <linearGradient id={gradientId2} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#f87171" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#f87171" stopOpacity={0.8} />
                  </linearGradient>
                  <linearGradient id={gradientId3} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#60a5fa" stopOpacity={0.8} />
                  </linearGradient>
                  <linearGradient id={gradientId4} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#fb923c" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#fb923c" stopOpacity={0.8} />
                  </linearGradient>
                </defs>
              </svg>
              
              {/* Quadrant labels - only show on larger screens */}
              {!isMobile && (
                <>
                  <div className="absolute top-14 left-16 z-10">
                    <div className="bg-white/70 backdrop-blur-sm px-2 py-1 rounded-lg shadow-sm border border-green-200 text-xs font-semibold text-green-800">
                      LOW DIFFICULTY + LOW TIME
                    </div>
                  </div>
                  <div className="absolute top-14 right-16 z-10">
                    <div className="bg-white/70 backdrop-blur-sm px-2 py-1 rounded-lg shadow-sm border border-orange-200 text-xs font-semibold text-orange-800">
                      HIGH DIFFICULTY + LOW TIME
                    </div>
                  </div>
                  <div className="absolute bottom-16 left-16 z-10">
                    <div className="bg-white/70 backdrop-blur-sm px-2 py-1 rounded-lg shadow-sm border border-blue-200 text-xs font-semibold text-blue-800">
                      LOW DIFFICULTY + HIGH TIME
                    </div>
                  </div>
                  <div className="absolute bottom-16 right-16 z-10">
                    <div className="bg-white/70 backdrop-blur-sm px-2 py-1 rounded-lg shadow-sm border border-red-200 text-xs font-semibold text-red-800">
                      HIGH DIFFICULTY + HIGH TIME
                    </div>
                  </div>
                </>
              )}
              
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart
                  margin={{ 
                    top: 20, 
                    right: isMobile ? 10 : 20, 
                    bottom: isMobile ? 60 : 40, 
                    left: isMobile ? 20 : 40 
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    type="number" 
                    dataKey="x" 
                    name="Difficulty" 
                    domain={[0, 100]}
                    tick={{ fill: '#1f2937', fontSize: isMobile ? 10 : 14 }}
                    label={{ 
                      value: 'Difficulty', 
                      position: 'bottom', 
                      fill: '#1f2937',
                      fontSize: isMobile ? 12 : 16,
                      offset: isMobile ? 40 : 20
                    }}
                  />
                  <YAxis 
                    type="number" 
                    dataKey="y" 
                    name="Time"
                    domain={[0, 100]}
                    tick={{ fill: '#1f2937', fontSize: isMobile ? 10 : 14 }}
                    label={{ 
                      value: 'Time', 
                      angle: -90, 
                      position: 'insideLeft', 
                      fill: '#1f2937',
                      fontSize: isMobile ? 12 : 16
                    }}
                  />
                  <ZAxis 
                    type="number" 
                    dataKey="z" 
                    range={[100, 1000]} 
                    name="Error Rate" 
                  />
                  
                  {/* Quadrant backgrounds */}
                  <rect x="0" y="0" width="50" height="50" fill={`url(#${gradientId1})`} opacity={0.3} />
                  <rect x="50" y="0" width="50" height="50" fill={`url(#${gradientId4})`} opacity={0.3} />
                  <rect x="0" y="50" width="50" height="50" fill={`url(#${gradientId3})`} opacity={0.3} />
                  <rect x="50" y="50" width="50" height="50" fill={`url(#${gradientId2})`} opacity={0.3} />
                  
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    wrapperStyle={{ outline: 'none', zIndex: 100 }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className={`bg-white p-3 border-2 border-gray-200 rounded-lg shadow-lg ${isMobile ? 'text-xs' : 'text-base'}`}>
                            <p className={`font-semibold ${isMobile ? 'text-sm' : 'text-lg'} mb-1 border-b pb-1`}>{data.subject}</p>
                            <p className="text-gray-700 my-1"><span className="font-medium">Difficulty:</span> {data.x}%</p>
                            <p className="text-gray-700 my-1"><span className="font-medium">Time:</span> {data.y}%</p>
                            <p className="text-gray-700 my-1"><span className="font-medium">Error Rate:</span> {data.z}%</p>
                            <p className="font-medium text-violet-700 mt-2 pt-1 border-t">
                              Cognitive Load: {data.cognitiveLoad}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  
                  <Scatter 
                    name="Cognitive Load" 
                    data={scatterData} 
                    fill={chartConfig.cognitiveLoad.color}
                    shape="circle"
                    strokeWidth={2}
                    stroke="#fff"
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 text-xs">
            <div className="flex items-center gap-2 p-2 bg-white rounded shadow-sm border border-gray-100">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: chartConfig.cognitiveLoad.color }}></span>
              <span className="font-medium text-gray-800">Cognitive Load:</span>
              <span className={`text-gray-600 ${isMobile ? 'text-[10px]' : ''}`}>Overall effort</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-white rounded shadow-sm border border-gray-100">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: chartConfig.difficulty.color }}></span>
              <span className="font-medium text-gray-800">Difficulty:</span>
              <span className={`text-gray-600 ${isMobile ? 'text-[10px]' : ''}`}>Complexity</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-white rounded shadow-sm border border-gray-100">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: chartConfig.timeIntensity.color }}></span>
              <span className="font-medium text-gray-800">Time:</span>
              <span className={`text-gray-600 ${isMobile ? 'text-[10px]' : ''}`}>Time spent</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-white rounded shadow-sm border border-gray-100">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: chartConfig.accuracyInverse.color }}></span>
              <span className="font-medium text-gray-800">Error Rate:</span>
              <span className={`text-gray-600 ${isMobile ? 'text-[10px]' : ''}`}>Mistakes %</span>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default CognitiveLoadIndex;
